import { useEffect, useState } from "react";
import { Alert, Button, Card, EmptyState, Icon, Typography } from "@/deps/ui-library";
import { api } from "@/api";
import type { CheckoutOrder, SubscriptionStatus } from "@/api/types";
import { useSignOut, useSwitchOrganization } from "@/api/queries";
import { useCanIfSignedIn, useSessionIfSignedIn } from "@/app/session";
import { BILLING_CONTACT } from "@/lib/billingContact";
import { formatDate } from "@/lib/dates";
import { Checkout } from "@/features/settings/subscription/Checkout";
import { PaymentPage } from "@/features/settings/subscription/PaymentPage";
import { saveFile } from "@/lib/download";

const HEADINGS: Record<string, { title: string; detail: string }> = {
  trial_ended: {
    title: "Your free trial has ended",
    detail: "Your books are kept, but locked. Subscribe to carry on where you left off.",
  },
  terminated: {
    title: "This subscription has ended",
    detail: "Your books are kept, but locked. Subscribe again to carry on where you left off.",
  },
  suspended: {
    title: "This subscription has been suspended",
    detail:
      "The renewal wasn't paid, so your books are locked. Settle the bill to carry on where you left off.",
  },
};

/**
 * What a locked company sees instead of the application: an ended trial, an unpaid bill, or a cancelled
 * subscription (suite ADR-0096). Only two things are left, and only for an administrator: subscribing, and
 * taking the books away.
 *
 * It answers what somebody locked out actually wants to know: what happened, what has become of the data,
 * and what to do. The second matters most, because "access is locked" reads as "your ledger may be gone",
 * and it isn't: everything is kept until the date shown.
 */
export function BooksLockedPage({
  status,
  onRecheck,
  rechecking,
}: {
  status: SubscriptionStatus | undefined;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  const can = useCanIfSignedIn();
  // Signing out and the other companies still work: a lock is this company's, not the person's.
  const session = useSessionIfSignedIn()?.session;
  const others = (session?.organizations ?? []).filter((o) => o.id !== session?.organization.id);
  const signOut = useSignOut();
  const switchTo = useSwitchOrganization();
  const administrator = can("settings.subscription.manage");
  const [exporting, setExporting] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [order, setOrder] = useState<CheckoutOrder | null>(null);
  const [paying, setPaying] = useState(false);

  // A suspended company pays what it owes on the payment page, and is served again once it's recorded (plan slice 10.7).
  const pay = async () => {
    setPaying(true);
    setRefused(null);
    try {
      setOrder(await api.subscription.payBill());
    } catch (failure) {
      setRefused(failure instanceof Error ? failure.message : "The payment couldn't be started.");
    } finally {
      setPaying(false);
    }
  };

  // Whoever pays does it somewhere else and comes back to this tab. Checking on every return is what
  // keeps this page from outliving the lock it describes.
  useEffect(() => {
    onRecheck();
    const recheck = () => onRecheck();
    window.addEventListener("focus", recheck);
    return () => window.removeEventListener("focus", recheck);
  }, [onRecheck]);

  const heading = HEADINGS[status?.messageCode ?? "suspended"] ?? HEADINGS.suspended!;

  const exportBooks = async () => {
    setExporting(true);
    setRefused(null);
    try {
      saveFile(await api.subscription.exportData(), "rigo-accounting-books.zip");
    } catch (error) {
      setRefused(error instanceof Error ? error.message : "The books couldn't be exported.");
    } finally {
      setExporting(false);
    }
  };

  // A trial that has ended is bought here: the shell is locked, so there's no page to go to. Paying re-checks the
  // status, and the provider takes this page down once it says the books are open.
  if (subscribing) {
    return (
      <main className="bg-canvas flex min-h-screen justify-center px-6 py-10">
        <div className="flex w-full max-w-[1100px] flex-col gap-4">
          <h1 className="text-foreground m-0 text-xl font-semibold">Subscribe</h1>
          <Checkout onDone={onRecheck} onCancel={() => setSubscribing(false)} />
        </div>
      </main>
    );
  }

  return (
    <main className="bg-canvas flex min-h-screen items-start justify-center px-6 py-16">
      <Card
        variant="outlined"
        size="none"
        className="flex w-full max-w-[560px] flex-col items-center gap-4 px-8 py-10 text-center"
      >
        <EmptyState.Indicator config={{ size: "sm", variant: "warning" }}>
          <Icon name="Lock" size="md" />
        </EmptyState.Indicator>

        <h1 className="text-foreground m-0 text-xl font-semibold">{heading.title}</h1>
        <p className="text-muted-foreground m-0 max-w-[420px] text-sm">{heading.detail}</p>

        <p className="text-foreground m-0 max-w-[420px] text-sm">
          <strong className="font-semibold">Nothing has been deleted.</strong>{" "}
          {status?.keptUntil
            ? `Every invoice, bill and journal is kept until ${formatDate(status.keptUntil)}.`
            : "Every invoice, bill and journal is kept exactly as it was."}
        </p>

        {administrator ? (
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              loading={paying}
              onClick={() => {
                if (status?.messageCode === "suspended") {
                  void pay();
                  return;
                }
                if (status?.messageCode === "trial_ended") {
                  setSubscribing(true);
                  return;
                }
                // A subscription that ended is subscribed to again through billing.
                window.location.href = `mailto:${BILLING_CONTACT.email}?subject=${encodeURIComponent(
                  "Subscribe to Rigo Accounting",
                )}`;
              }}
            >
              {status?.messageCode === "suspended" ? "Pay" : "Subscribe"}
            </Button>
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              leftIcon="Download"
              loading={exporting}
              onClick={() => void exportBooks()}
            >
              Export data
            </Button>
          </div>
        ) : (
          <Alert
            role="status"
            className="mt-2 px-5 py-4 text-left"
            config={{ variant: "subtle", colorScheme: "default" }}
            description="Ask an administrator to subscribe, or to export the books."
          />
        )}

        {refused ? (
          <p role="alert" className="text-bad-text m-0 text-sm">
            {refused}
          </p>
        ) : null}

        {administrator ? (
          <Typography.Text as="p" variant="small" muted className="m-0">
            The export is a zip of CSV files: the chart of accounts, every journal line, and your
            customers and suppliers. For anything else, write to {BILLING_CONTACT.email}, or call{" "}
            {BILLING_CONTACT.phone}.
          </Typography.Text>
        ) : null}

        <div className="flex flex-wrap justify-center gap-2">
          <Button
            colorScheme="default"
            variant="ghost"
            size="sm"
            leftIcon="RefreshCw"
            loading={rechecking}
            onClick={onRecheck}
          >
            Check again
          </Button>
          {others.map((organization) => (
            <Button
              key={organization.id}
              colorScheme="default"
              variant="ghost"
              size="sm"
              leftIcon="Building2"
              disabled={switchTo.isPending}
              onClick={() => switchTo.mutate(organization.id)}
            >
              {`Open ${organization.name}`}
            </Button>
          ))}
          {session ? (
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              leftIcon="LogOut"
              disabled={signOut.isPending}
              onClick={() => signOut.mutate()}
            >
              Sign out
            </Button>
          ) : null}
        </div>
      </Card>

      {order ? (
        <PaymentPage
          order={order}
          replace={(method) => api.subscription.payBill(null, method)}
          onClose={() => setOrder(null)}
          onPaid={() => {
            setOrder(null);
            onRecheck();
          }}
        />
      ) : null}
    </main>
  );
}
